import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Input, Button, message, Alert } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import insuranceApi from "api/insuranceApi";
import ApplicationListItem from "./ApplicationListItem";

/**
 * ApplicationList — displays the full list of insurance applications
 * with a reference number lookup and "Buy Another" button.
 *
 * Requirements: 2.3, 3.6, 7.1, 7.2
 */
export default function ApplicationList({
  applications,
  onBuyAnother,
  onLookupResult,
}) {
  const [lookupRef, setLookupRef] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  const handleLookup = useCallback(async () => {
    const trimmed = lookupRef.trim();
    if (!trimmed) return;

    setLookupLoading(true);
    setLookupResult(null);
    setLookupError(null);

    try {
      const response = await insuranceApi.getApplication(trimmed);
      const data = response?.data?.data || response?.data;
      setLookupResult(data);
      if (onLookupResult) {
        onLookupResult(data);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "ไม่พบใบสมัครที่ตรงกับหมายเลขอ้างอิงนี้ (No application found for this reference number)";
      setLookupError(errorMessage);
    } finally {
      setLookupLoading(false);
    }
  }, [lookupRef, onLookupResult]);

  return (
    <div style={{ padding: "16px", maxWidth: 800, margin: "0 auto" }}>
      <h2>รายการใบสมัครประกันภัย (Insurance Applications)</h2>

      {/* Reference number lookup */}
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="ค้นหาด้วยหมายเลขอ้างอิง (Search by reference number)"
          value={lookupRef}
          onChange={(e) => {
            setLookupRef(e.target.value);
            setLookupError(null);
            setLookupResult(null);
          }}
          onSearch={handleLookup}
          enterButton={
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={lookupLoading}
            >
              ค้นหา
            </Button>
          }
          loading={lookupLoading}
        />
      </div>

      {/* Lookup result display */}
      {lookupError && (
        <Alert
          type="error"
          message={lookupError}
          showIcon
          closable
          onClose={() => setLookupError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {lookupResult && (
        <div
          style={{
            marginBottom: 16,
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <ApplicationListItem
            referenceNumber={lookupResult.referenceNumber}
            status={lookupResult.status}
            submittedAt={lookupResult.submittedAt}
            insuranceStartDate={lookupResult.insuranceStartDate}
          />
        </div>
      )}

      {/* Application list */}
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          marginBottom: 16,
        }}
      >
        {applications.map((app) => (
          <ApplicationListItem
            key={app.referenceNumber}
            referenceNumber={app.referenceNumber}
            status={app.status}
            submittedAt={app.submittedAt}
            insuranceStartDate={app.insuranceStartDate}
          />
        ))}
      </div>

      {/* Buy Another Insurance button */}
      <Button type="primary" size="large" block onClick={onBuyAnother}>
        ซื้อประกันภัยเพิ่ม (Buy Another Insurance)
      </Button>
    </div>
  );
}

ApplicationList.propTypes = {
  applications: PropTypes.arrayOf(
    PropTypes.shape({
      referenceNumber: PropTypes.string.isRequired,
      status: PropTypes.oneOf([
        "waiting_for_payment",
        "pending",
        "completed",
        "rejected",
      ]).isRequired,
      submittedAt: PropTypes.string.isRequired,
      insuranceStartDate: PropTypes.string,
    })
  ).isRequired,
  onBuyAnother: PropTypes.func.isRequired,
  onLookupResult: PropTypes.func,
};
